import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Sparkles, 
  Clock, 
  Save, 
  Upload, 
  Plus, 
  Trash2, 
  Edit3, 
  Eye, 
  X, 
  RefreshCw,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function HighlightsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [sectionId, setSectionId] = useState(36);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);

  // Edit/Add item modal state
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingMode, setEditingMode] = useState(null); // 'add' or 'edit'
  const [tempItem, setTempItem] = useState(null);

  // Lightbox preview modal state
  const [lightboxImage, setLightboxImage] = useState(null);

  // Media map from database
  const [mediaMap, setMediaMap] = useState({});

  const fetchMediaMap = useCallback(async () => {
    try {
      const response = await fetch(
        `${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(media => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path;
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.warn('Media fetch error:', err.message);
    }
  }, [apiBase]);

  const getImageUrl = (mediaId) => {
    if (!mediaId) return null;
    if (mediaMap[mediaId]) return mediaMap[mediaId];
    return resolveMediaSrc(mediaId, mediaMap) || mediaId;
  };

  // Form values
  const [formData, setFormData] = useState({
    kicker: 'Highlights',
    title: 'Today on campus',
    intro: 'Live updates and key moments as they happen across school life.',
    live_label: 'Live update',
    items: []
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    intro: false,
    live_label: false,
  });

  // Track original values
  const [originalData, setOriginalData] = useState({ ...formData });

  // Track item operations
  const [itemOperations, setItemOperations] = useState({});

  const loadHighlightsSection = useCallback(async () => {
    setLoading(true);
    try {
      let response = await fetch(
        `${apiBase}/cms/section/highlights/with-pending?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        response = await fetch(
          `${apiBase}/cms/section/36/with-pending?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      if (!response.ok) {
        response = await fetch(`${apiBase}/homepage/cms/sections?published=true&_t=${Date.now()}`, {
          credentials: 'omit'
        });
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      let section = null;
      let pending_changes = [];

      if (result.success && result.data) {
        section = result.data.section;
        pending_changes = result.data.pending_changes || [];
      } else if (result.ok && Array.isArray(result.data)) {
        section = result.data.find(s => s.section_type === 'highlights' || s.id === 36);
      }

      if (section) {
        let published = section.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }

        let draft = section.content;
        if (typeof draft === 'string') {
          try { draft = JSON.parse(draft); } catch (e) { draft = {}; }
        }

        const source = published?.en || published?.id || draft?.en || draft?.id || published || draft || {};

        // Clean items: Only take items with actual content (filter out blank/empty placeholder items)
        const rawItems = Array.isArray(source.items) ? source.items : [];
        const cleanItems = rawItems.filter(it => it && typeof it === 'object' && (it.title || it.summary || it.media_id || it.image));

        const initialData = {
          kicker: source.kicker || 'Highlights',
          title: source.title || 'Today on campus',
          intro: source.intro || 'Live updates and key moments as they happen across school life.',
          live_label: source.live_label || 'Live update',
          items: cleanItems
        };

        const pendingMap = {
          kicker: false,
          title: false,
          intro: false,
          live_label: false
        };

        const itemOpsMap = {};

        // Query global pending changes queue for section 36
        try {
          const appRes = await fetch(`${apiBase}/homepage/cms/pending-approvals?limit=50`, {
            credentials: 'include'
          });
          if (appRes.ok) {
            const appJson = await appRes.json();
            const items = appJson.data?.items || [];
            items.forEach(item => {
              if ((item.section_id === section.id || item.section_type === 'highlights') && !pending_changes.some(p => p.id === item.id)) {
                pending_changes.push(item);
              }
            });
          }
        } catch (_) {}

        // When field change is pending, display proposed/new value
        if (Array.isArray(pending_changes)) {
          pending_changes.forEach(change => {
            if (change.status === 'pending' && (change.locale === 'en' || !change.locale)) {
              let val = change.new_value;
              if (typeof val === 'string') {
                try { val = JSON.parse(val); } catch (e) {}
              }

              // Header fields
              if (change.field_id in pendingMap) {
                pendingMap[change.field_id] = true;
                if (val !== undefined && val !== null) {
                  initialData[change.field_id] = val;
                }
              }

              // Item operations (items.0.add, items.1.update, items.2.delete, items.0)
              if (change.field_id && change.field_id.includes('items.')) {
                const match = change.field_id.match(/items\.(\d+)(?:\.(add|update|delete))?/);
                if (match) {
                  const itemIdx = parseInt(match[1], 10);
                  const op = match[2] || 'update';

                  if (val !== undefined && val !== null) {
                    if (op === 'update' && initialData.items[itemIdx]) {
                      initialData.items[itemIdx] = { ...initialData.items[itemIdx], ...val };
                      itemOpsMap[itemIdx] = { operation: op, status: 'pending' };
                    } else if (op === 'add') {
                      // Avoid pushing duplicate if item already exists by ID or title
                      const existingIdx = initialData.items.findIndex(it => 
                        (val.id && it.id === val.id) || 
                        (val.title && it.title && it.title.trim().toLowerCase() === val.title.trim().toLowerCase())
                      );
                      if (existingIdx >= 0) {
                        initialData.items[existingIdx] = { ...initialData.items[existingIdx], ...val };
                        itemOpsMap[existingIdx] = { operation: 'add', status: 'pending' };
                      } else {
                        const targetIdx = initialData.items.length;
                        initialData.items.push(val);
                        itemOpsMap[targetIdx] = { operation: 'add', status: 'pending' };
                      }
                    } else if (op === 'delete') {
                      if (initialData.items[itemIdx]) {
                        itemOpsMap[itemIdx] = { operation: 'delete', status: 'pending' };
                      }
                    }
                  }
                }
              }
            }
          });
        }

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingMap);
        setItemOperations(itemOpsMap);
        setSectionId(section.id || 36);
      }
    } catch (err) {
      toastRef.current?.error('Error loading highlights section: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadHighlightsSection();
    fetchMediaMap();
  }, [loadHighlightsSection, fetchMediaMap]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleAddItem = () => {
    const newItem = {
      id: `item_new_${Date.now()}`,
      title: '',
      summary: '',
      media_id: null,
      image: null,
      photo: null
    };
    setTempItem(newItem);
    setEditingMode('add');
    setEditingItemIndex(null);
  };

  const handleEditItem = (index) => {
    const currentItem = formData.items[index] || {};
    setTempItem({
      ...currentItem,
      image: currentItem.image || currentItem.photo || (currentItem.media_id ? getImageUrl(currentItem.media_id) : null)
    });
    setEditingMode('edit');
    setEditingItemIndex(index);
  };

  const handleDeleteItem = (index) => {
    const item = formData.items[index];
    if (window.confirm(`Submit deletion of highlight "${item?.title || 'Untitled'}" for Principal review?`)) {
      setItemOperations(prev => ({
        ...prev,
        [index]: { operation: 'delete', status: 'pending_submission' }
      }));
      handleSubmitItemOperation(index, 'delete', item);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toastRef.current?.error(`Image max 10MB, your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toastRef.current?.error('Only JPG, PNG or WebP image files are allowed');
      return;
    }

    setImageUploading(true);
    setImageProgress(0);

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percent = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const resp = JSON.parse(xhr.responseText);
            if (resp.success || resp.ok) {
              const data = resp.data || resp;
              const filename = data.filename || data.file_name || '';
              const mediaId = data.media_id || filename;
              let directUrl = data.url || data.path || data.file_path || (filename ? `/api/uploads/images/${filename}` : '');
              if (directUrl && directUrl.startsWith('/uploads/') && !directUrl.startsWith('/api/uploads/')) {
                directUrl = `/api${directUrl}`;
              }
              const fullUrl = directUrl.startsWith('http')
                ? directUrl
                : `${window.location.origin}${directUrl.startsWith('/') ? '' : '/'}${directUrl}`;

              setTempItem(prev => ({
                ...prev,
                media_id: mediaId,
                image: fullUrl,
                photo: fullUrl,
                url: fullUrl
              }));
              toastRef.current?.success('Image uploaded successfully');
            } else {
              toastRef.current?.error(resp.error || resp.message || 'Upload failed');
            }
          } catch (pe) {
            toastRef.current?.error('Invalid server response');
          }
        } else {
          toastRef.current?.error(`Upload failed (${xhr.status})`);
        }
        setImageUploading(false);
        setImageProgress(0);
      });

      xhr.addEventListener('error', () => {
        toastRef.current?.error('Upload connection error');
        setImageUploading(false);
        setImageProgress(0);
      });

      xhr.open('POST', `${apiBase}/media/upload`);
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
      if (savedToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${savedToken}`);
      }
      xhr.withCredentials = true;
      xhr.send(uploadData);
    } catch (err) {
      toastRef.current?.error('Upload error: ' + err.message);
      setImageUploading(false);
      setImageProgress(0);
    }
  };

  // Submit scalar field changes (Single submission)
  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toastRef.current?.warning('No changes detected for this field');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
      const reqHeaders = { 'Content-Type': 'application/json' };
      if (savedToken) {
        reqHeaders['Authorization'] = `Bearer ${savedToken}`;
      }

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: reqHeaders,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || result.ok) {
        toastRef.current?.success(`Changes for ${fieldName.replace(/_/g, ' ')} submitted for Principal review`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        if (response.status === 409 || result?.message?.includes('already has a pending')) {
          toastRef.current?.info('This field is already awaiting approval in the review queue');
        } else {
          toastRef.current?.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toastRef.current?.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Submit item operation (Add, Update, Delete)
  const handleSubmitItemOperation = async (index, operation, overrideItem = null) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not loaded');
      return;
    }

    const item = overrideItem || tempItem;
    if (!item && operation !== 'delete') {
      toastRef.current?.error('Item data missing');
      return;
    }

    const subKey = `item_${index}_${operation}`;
    setSubmitting(prev => ({ ...prev, [subKey]: true }));

    try {
      let fieldId, fieldName, oldValue, newValue;

      if (operation === 'delete') {
        fieldId = `items.${index}.delete`;
        fieldName = `Delete Highlight: ${item?.title || 'Untitled'}`;
        oldValue = item || null;
        newValue = null;
      } else if (operation === 'add') {
        fieldId = `items.${index}.add`;
        fieldName = `Add Highlight: ${item.title || 'New Item'}`;
        oldValue = null;
        newValue = item;
      } else if (operation === 'update') {
        fieldId = `items.${index}.update`;
        fieldName = `Update Highlight: ${item.title || 'Untitled'}`;
        oldValue = originalData.items?.[index] || null;
        newValue = item;
      }

      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
      const reqHeaders = { 'Content-Type': 'application/json' };
      if (savedToken) {
        reqHeaders['Authorization'] = `Bearer ${savedToken}`;
      }

      console.log('[HighlightsForm] Submitting item operation:', { sectionId, fieldId, operation, newValue });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: reqHeaders,
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: oldValue,
            new_value: newValue,
            locale: 'en',
            item_id: item?.id || null,
            operation_type: operation
          })
        }
      );

      const result = await response.json();
      console.log('[HighlightsForm] Submit response status:', response.status, result);

      if (result.success || result.ok) {
        setEditingMode(null);
        setEditingItemIndex(null);
        setTempItem(null);
        toastRef.current?.success(`Highlight ${operation} submitted for Principal review`);
        // Refresh directly from server to prevent duplicate rendering and sync pending badges
        await loadHighlightsSection();
      } else {
        if (response.status === 409 || result?.message?.includes('already has a pending')) {
          toastRef.current?.info('This item is already awaiting approval in the review queue');
        } else {
          toastRef.current?.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[HighlightsForm] Submit error:', err);
      toastRef.current?.error('Error submitting item: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [subKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading highlights section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header & Actions */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Sparkles className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="unified-header-text">
            <h1 className="unified-title">Highlights Section</h1>
            <p className="unified-subtitle">Manage campus daily activities, live updates, and featured highlight cards</p>
          </div>
        </div>

        <div className="unified-header-actions">
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={loadHighlightsSection}
            title="Reload data from server"
          >
            <RefreshCw size={14} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="unified-card">
        {/* Section Header Controls */}
        <div>
          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', marginBottom: '16px', letterSpacing: '-0.01em' }}>
            Section Header Settings
          </h3>

          {/* Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Section Kicker</label>
                <p className="unified-field-desc">Small category label above the main heading (e.g. "Highlights")</p>
              </div>
              {pendingFields.kicker && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="50"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={pendingFields.kicker}
                placeholder="e.g. Highlights"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> <span>{submitting.kicker ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Main Heading Title</label>
                <p className="unified-field-desc">Prominent headline for the campus highlights section</p>
              </div>
              {pendingFields.title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="100"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={pendingFields.title}
                placeholder="e.g. Today on campus"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> <span>{submitting.title ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>

          {/* Intro */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Introductory Summary</label>
                <p className="unified-field-desc">Brief narrative introducing the daily campus moments</p>
              </div>
              {pendingFields.intro && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <textarea
                maxLength="500"
                rows={2}
                className="unified-textarea"
                value={formData.intro}
                onChange={(e) => handleFieldChange('intro', e.target.value)}
                disabled={pendingFields.intro}
                placeholder="e.g. Live updates and key moments as they happen across school life."
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('intro')}
                disabled={submitting.intro || pendingFields.intro || formData.intro === originalData.intro}
              >
                <Save size={14} /> <span>{submitting.intro ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>

          {/* Live Label */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Live Indicator Badge Label</label>
                <p className="unified-field-desc">Text shown in the pulsating live status pill (e.g. "Live update")</p>
              </div>
              {pendingFields.live_label && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="50"
                className="unified-input"
                value={formData.live_label}
                onChange={(e) => handleFieldChange('live_label', e.target.value)}
                disabled={pendingFields.live_label}
                placeholder="e.g. Live update"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('live_label')}
                disabled={submitting.live_label || pendingFields.live_label || formData.live_label === originalData.live_label}
              >
                <Save size={14} /> <span>{submitting.live_label ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>

          </div>

        {/* Highlights Items List */}
        <div style={{ marginTop: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', margin: 0, letterSpacing: '-0.01em' }}>
                Campus Highlight Cards ({formData.items.length})
              </h3>
              <p style={{ fontSize: '13px', color: '#64748b', margin: '3px 0 0' }}>
                Rotating cards with photographs and narrative descriptions showcasing daily campus life
              </p>
            </div>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleAddItem}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Plus size={15} /> <span>Add New Highlight Card</span>
            </button>
          </div>

          {formData.items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1' }}>
              <ImageIcon size={40} color="#94a3b8" style={{ margin: '0 auto 12px', display: 'block' }} />
              <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#334155', margin: '0 0 6px' }}>No highlight cards yet</h4>
              <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Click "Add New Highlight Card" above to publish your first campus moment.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '18px' }}>
              {formData.items
                .filter(item => item && typeof item === 'object' && (item.title || item.summary || item.media_id || item.image))
                .map((item, idx) => {
                const op = itemOperations[idx];
                const isPending = op?.status === 'pending';
                const imgSrc = getImageUrl(item.media_id || item.image);

                return (
                  <div
                    key={item.id || idx}
                    style={{
                      border: isPending ? '1.5px solid #f59e0b' : '1px solid #e2e8f0',
                      borderRadius: '14px',
                      overflow: 'hidden',
                      background: isPending ? '#fffdf0' : '#ffffff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Card Image Thumbnail with Click-to-Modal */}
                    <div style={{ position: 'relative', height: '170px', background: '#0f172a', overflow: 'hidden' }}>
                      {imgSrc ? (
                        <img
                          src={imgSrc}
                          alt={item.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => setLightboxImage({ src: imgSrc, title: item.title, id: item.media_id })}
                        />
                      ) : (
                        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', gap: '8px' }}>
                          <ImageIcon size={26} />
                          <span style={{ fontSize: '13px', fontWeight: '500' }}>No photograph</span>
                        </div>
                      )}

                      {/* Click to zoom pill */}
                      {imgSrc && (
                        <button
                          type="button"
                          onClick={() => setLightboxImage({ src: imgSrc, title: item.title, id: item.media_id })}
                          style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(15, 23, 42, 0.75)',
                            backdropFilter: 'blur(4px)',
                            color: '#ffffff',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            padding: '4px 10px',
                            borderRadius: '20px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          <Eye size={12} /> View Full
                        </button>
                      )}

                      {isPending && (
                        <div style={{ position: 'absolute', top: '8px', left: '8px', zIndex: 2 }}>
                          <span className="unified-pending-pill" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
                            <Clock size={11} /> Pending {op.operation ? op.operation.toUpperCase() : 'REVIEW'}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Card Content */}
                    <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 6px', lineHeight: 1.4 }}>
                        {item.title || 'Untitled Card'}
                      </h4>
                      <p style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.5', margin: '0 0 16px', flex: 1 }}>
                        {item.summary || 'No summary text provided.'}
                      </p>

                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' }}>
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => handleEditItem(idx)}
                          disabled={isPending}
                          style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', padding: '7px' }}
                        >
                          <Edit3 size={13} /> <span>Edit Card</span>
                        </button>
                        <button
                          type="button"
                          className="unified-secondary-btn"
                          onClick={() => handleDeleteItem(idx)}
                          disabled={isPending}
                          title="Delete card"
                          style={{ color: '#ef4444', borderColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '12px', padding: '7px 12px' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Item Edit / Add Modal */}
      {(editingMode || tempItem) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.7)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => {
            setEditingMode(null);
            setEditingItemIndex(null);
            setTempItem(null);
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '560px',
              width: '100%',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              border: '1px solid #e2e8f0'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={18} color="#4f46e5" />
                <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0, letterSpacing: '-0.01em' }}>
                  {editingMode === 'add' ? 'Add New Highlight Card' : 'Edit Highlight Card'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingMode(null);
                  setEditingItemIndex(null);
                  setTempItem(null);
                }}
                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '22px', maxHeight: '75vh', overflowY: 'auto' }}>
              {/* Photo Preview & Upload */}
              <div style={{ marginBottom: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label className="unified-label" style={{ margin: 0 }}>
                    Highlight Photograph <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  {(tempItem?.image || tempItem?.media_id) && (
                    <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: '700' }}>✓ Foto Terpasang</span>
                  )}
                </div>

                {(tempItem?.image || tempItem?.media_id) ? (
                  <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', height: '190px', background: '#0f172a', marginBottom: '12px', border: '1px solid #e2e8f0' }}>
                    <img
                      src={tempItem.image || getImageUrl(tempItem.media_id)}
                      alt="Preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{ position: 'absolute', bottom: '8px', left: '8px', background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)', color: '#fff', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                      {String(tempItem.media_id || tempItem.image)}
                    </div>
                  </div>
                ) : (
                  <div style={{ height: '130px', border: '2px dashed #cbd5e1', borderRadius: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', color: '#64748b', marginBottom: '12px', background: '#f8fafc' }}>
                    <ImageIcon size={30} color="#94a3b8" />
                    <span style={{ fontSize: '13px', fontWeight: '500' }}>Belum ada foto yang dipilih</span>
                    <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>* Wajib memilih/mengunggah foto kartu highlight</span>
                  </div>
                )}

                <label
                  htmlFor="highlight-modal-upload"
                  className={`unified-secondary-btn ${imageUploading ? 'disabled' : ''}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: imageUploading ? 'not-allowed' : 'pointer', fontSize: '13px', padding: '8px 14px' }}
                >
                  <Upload size={15} />
                  <span>{imageUploading ? `Uploading ${imageProgress}%...` : ((tempItem?.image || tempItem?.media_id) ? 'Ganti Foto Highlight' : 'Pilih & Upload Foto')}</span>
                  <input
                    id="highlight-modal-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                      handleImageUpload(e);
                      e.target.value = '';
                    }}
                    disabled={imageUploading}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              {/* Title Field */}
              <div style={{ marginBottom: '16px' }}>
                <label className="unified-label">Card Title <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  maxLength="100"
                  className="unified-input"
                  value={tempItem?.title || ''}
                  onChange={(e) => setTempItem(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g. Morning assembly and student leadership"
                />
              </div>

              {/* Summary Field */}
              <div>
                <label className="unified-label">Card Narrative Summary</label>
                <textarea
                  maxLength="300"
                  rows={3}
                  className="unified-textarea"
                  value={tempItem?.summary || ''}
                  onChange={(e) => setTempItem(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Brief description of the daily moment, activity, or student achievement..."
                  style={{ minHeight: '80px' }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 22px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <button
                type="button"
                className="unified-secondary-btn"
                onClick={() => {
                  setEditingMode(null);
                  setEditingItemIndex(null);
                  setTempItem(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => {
                  if (!tempItem?.title?.trim()) {
                    toastRef.current?.error('Judul kartu highlight wajib diisi');
                    return;
                  }
                  if (!tempItem?.media_id && !tempItem?.image) {
                    toastRef.current?.error('Foto highlight wajib diunggah sebelum submit');
                    return;
                  }
                  const op = editingMode === 'add' ? 'add' : 'update';
                  const targetIdx = editingMode === 'add' ? formData.items.length : editingItemIndex;
                  handleSubmitItemOperation(targetIdx, op);
                }}
                disabled={!tempItem?.title?.trim() || (!tempItem?.media_id && !tempItem?.image) || imageUploading}
              >
                <Save size={14} /> <span>{editingMode === 'add' ? 'Submit New Highlight' : 'Submit Card Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Image Preview Modal */}
      {lightboxImage && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0, 0, 0, 0.88)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
          onClick={() => setLightboxImage(null)}
        >
          <div
            style={{
              background: '#0f172a',
              borderRadius: '16px',
              maxWidth: '850px',
              width: '100%',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', color: '#fff' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>{lightboxImage.title || lightboxImage.id || 'Image Preview'}</span>
              <button
                type="button"
                onClick={() => setLightboxImage(null)}
                style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: '12px', background: '#000', textAlign: 'center' }}>
              <img
                src={lightboxImage.src}
                alt={lightboxImage.title}
                style={{ maxWidth: '100%', maxHeight: '560px', objectFit: 'contain', margin: '0 auto', display: 'block', borderRadius: '8px' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
